'use client'

import { CheckIcon, ChevronDownIcon, InfoIcon, Loader2Icon, PlusIcon } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import Link from 'next/link'
import { type ReactNode, useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  type CapabilityHighlight,
  capabilityHighlights,
} from '@/features/billing/lib/capability-highlights'
import { formatStripeAmount } from '@/features/billing/lib/format'
import { splitUpsellTeaser } from '@/features/billing/lib/upsell-teaser'
import { planLevelOrder } from '@/lib/billing/capabilities'
import { cn } from '@/lib/utils'
import { AlgoType } from '@/types/generated/algo'
import { PlanLevel } from '@/types/generated/common/account'
import { type Capabilities, type Plan, type Price } from '@/types/generated/public_api'

/**
 * How a plan card's call-to-action behaves:
 * - `current` — the viewer is already on this plan (disabled marker)
 * - `checkout` — signed-in upgrade; starts Stripe Checkout for the picked price
 * - `managed` — viewer has a live subscription; tier/interval changes go through
 *   the Billing Portal, so checkout is disabled on every card
 * - `link` — public pricing page; routes to sign-up / login
 */
export type PlanCardCta =
  | { kind: 'current' }
  | { kind: 'checkout'; choosingPriceId: string | null; onChoose: (price: Price) => void }
  | { kind: 'managed' }
  | { kind: 'link'; href: string; label: string }

interface PlanCardProps {
  plan: Plan
  plans: Plan[]
  interval: string
  /** The viewer's capabilities when signed in — drives the ✓/↑ "in your plan" markers. */
  userCapabilities?: Capabilities
  /** Viewer can still claim their one-time free trial — shows the trial badge/CTA. */
  trialEligible?: boolean
  highlighted?: boolean
  cta: PlanCardCta
  /** Below md the grid shows one plan at a time (PlansGrid's plan switcher); the rest hide. */
  hiddenOnMobile?: boolean
}

function pickPrice(plan: Plan, interval: string): Price | undefined {
  return plan.prices.find((p) => p.interval === interval)
}

interface FeatureListItem {
  key: string
  text: string
  infoItems?: FeatureInfoItem[]
  infoLabel?: string
  infoTitle?: string
}

interface FeatureInfoItem {
  key: string
  label: string
  isNew?: boolean
  newLabel?: string
}

function compareHighlights(base: CapabilityHighlight[], target: CapabilityHighlight[]) {
  const baseByKey = new Map(base.map((row) => [row.key, row]))

  return target.filter((row) => {
    const mine = baseByKey.get(row.key)
    if (row.kind === 'count') {
      return row.count > (mine?.kind === 'count' ? mine.count : 0)
    }

    return row.value && !(mine?.kind === 'bool' && mine.value)
  })
}

/**
 * Keyed on the plan *level*, not `plan.label`: the label is product copy the
 * backend renames at will (FREE ships as "Essential"), and a label lookup would
 * drop the description on the next rename with nothing failing.
 */
const PLAN_DESCRIPTION_KEYS = {
  [PlanLevel.PLAN_LEVEL_FREE]: 'planDescriptions.free',
  [PlanLevel.PLAN_LEVEL_BASIC]: 'planDescriptions.basic',
  [PlanLevel.PLAN_LEVEL_PRO]: 'planDescriptions.pro',
  [PlanLevel.PLAN_LEVEL_ENTERPRISE]: 'planDescriptions.enterprise',
} as const

/**
 * Static bullets for the drafted Enterprise card. The tier has no entitlements
 * bucket, so the backend sends it with no `capabilities` at all — a computed
 * diff against Pro would render nothing. Swap these for the computed highlights
 * once Enterprise actually ships.
 */
const ENTERPRISE_HIGHLIGHT_KEYS = [
  'enterpriseHighlights.permissions',
  'enterpriseHighlights.riskLimits',
] as const

const PAYMENT_BENEFIT_KEYS = {
  cancelAnytime: 'paymentBenefits.cancelAnytime',
  securePayments: 'paymentBenefits.securePayments',
  noCommitment: 'paymentBenefits.noCommitment',
} as const

const ALGO_NAME_KEYS = {
  [AlgoType.ALGO_TYPE_MARKET]: 'algoNames.market',
  [AlgoType.ALGO_TYPE_LIMIT]: 'algoNames.limit',
  [AlgoType.ALGO_TYPE_TWAP]: 'algoNames.twap',
  [AlgoType.ALGO_TYPE_ADVANCED_TWAP]: 'algoNames.advancedTwap',
  [AlgoType.ALGO_TYPE_LIMIT_CHASE]: 'algoNames.limitChase',
  [AlgoType.ALGO_TYPE_HEDGE]: 'algoNames.hedge',
  [AlgoType.ALGO_TYPE_SINGLE_HEDGE]: 'algoNames.singleHedge',
  [AlgoType.ALGO_TYPE_TRIGGER]: 'algoNames.trigger',
  [AlgoType.ALGO_TYPE_RFQ]: 'algoNames.rfq',
  [AlgoType.ALGO_TYPE_CROSS_EXCHANGE_ARB]: 'algoNames.arb',
  [AlgoType.ALGO_TYPE_SPREADER]: 'algoNames.spreader',
  [AlgoType.ALGO_TYPE_SCALE]: 'algoNames.scale',
} as const

function FeatureItem({
  icon = <CheckIcon className="text-foreground/60 size-4 shrink-0" aria-hidden />,
  item,
}: {
  icon?: ReactNode
  item: FeatureListItem
}) {
  return (
    <li className="text-foreground/80 flex items-center gap-4 text-sm">
      {icon}
      <span>{item.text}</span>
      {item.infoItems && item.infoItems.length > 0 ? (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={item.infoLabel}
              className="text-foreground/40 hover:text-foreground relative inline-flex size-4 shrink-0 items-center justify-center rounded-full transition-colors before:absolute before:-inset-3 before:content-['']"
            >
              <InfoIcon className="size-3.5" aria-hidden />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="border-foreground/10 bg-popover/95 text-foreground w-64 border p-4 shadow-2xl backdrop-blur-xl"
          >
            <div className="flex flex-col gap-3">
              <p className="text-sm font-medium">{item.infoTitle}</p>
              <ul className="grid gap-2">
                {item.infoItems.map((info) => (
                  <li
                    key={info.key}
                    className={cn(
                      'flex items-center gap-2 text-sm',
                      info.isNew ? 'text-ox-cyan' : 'text-foreground/70'
                    )}
                  >
                    <CheckIcon
                      className={cn(
                        'size-3.5 shrink-0',
                        info.isNew ? 'text-ox-cyan' : 'text-foreground/40'
                      )}
                      aria-hidden
                    />
                    <span>{info.label}</span>
                    {info.isNew ? (
                      <span className="bg-ox-cyan/15 text-ox-cyan px-1 py-0.5 text-[10px] leading-none font-medium">
                        {info.newLabel}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          </PopoverContent>
        </Popover>
      ) : null}
    </li>
  )
}

const upsellIcon = <PlusIcon className="text-ox-cyan size-4 shrink-0" aria-hidden />

/**
 * A lower plan's "Available in <next plan>" list as a teaser: the first features stay
 * visible as the hook, the rest sit behind a "+ N more" toggle. The next plan's own card
 * lists them all beside it, so repeating the full list only made the page scroll.
 */
function UpsellTeaser({ items, planLabel }: { items: FeatureListItem[]; planLabel: string }) {
  const t = useTranslations('billing')
  const [expanded, setExpanded] = useState(false)
  const { shown, hidden } = splitUpsellTeaser(items)
  const visible = expanded ? items : shown
  return (
    <>
      <ul className="flex flex-col gap-2">
        {visible.map((item) => (
          <FeatureItem key={item.key} icon={upsellIcon} item={item} />
        ))}
      </ul>
      {hidden.length > 0 ? (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          className="text-ox-cyan hover:text-foreground flex w-fit items-center gap-1.5 text-sm transition-colors"
        >
          {expanded ? t('showLess') : t('moreInPlan', { count: hidden.length, plan: planLabel })}
          <ChevronDownIcon
            className={cn('size-4 transition-transform', expanded && 'rotate-180')}
            aria-hidden
          />
        </button>
      ) : null}
    </>
  )
}

export function PlanCard({
  plan,
  plans,
  interval,
  trialEligible,
  highlighted,
  cta,
  hiddenOnMobile = false,
}: PlanCardProps) {
  const t = useTranslations('billing')
  const locale = useLocale()
  const price = pickPrice(plan, interval)
  const displayCurrency = price?.currency ?? plan.prices[0]?.currency ?? 'usd'
  const displayPrice = formatStripeAmount(price?.unitAmount ?? '0', displayCurrency, locale)
  const displayInterval =
    interval === 'year' ? t('perYear') : interval === 'month' ? t('perMonth') : interval
  // Advertise the one-time trial only when this plan offers one and the viewer
  // hasn't used theirs (backend enforces the one-time rule regardless).
  const trialOffer = plan.trialPeriodDays > 0 && trialEligible === true
  const highlights = useMemo(
    () => (plan.capabilities ? capabilityHighlights(plan.capabilities) : []),
    [plan.capabilities]
  )
  // Every plan, including ones with no `capabilities` (ENTERPRISE has none):
  // this list answers "which column am I in". Filtering on that field would drop
  // the drafted card out entirely and make it look like the *first* plan.
  const orderedPlans = useMemo(
    () => [...plans].sort((a, b) => planLevelOrder(a.level) - planLevelOrder(b.level)),
    [plans]
  )
  const currentPlanIndex = orderedPlans.findIndex((item) => item.level === plan.level)
  const previousPlan = currentPlanIndex > 0 ? orderedPlans[currentPlanIndex - 1] : undefined
  const nextPlan =
    currentPlanIndex >= 0 && currentPlanIndex < orderedPlans.length - 1
      ? orderedPlans[currentPlanIndex + 1]
      : undefined
  const previousPlanLabel = previousPlan?.label
  const nextPlanLabel = nextPlan?.label
  const baseHighlights = previousPlan?.capabilities
    ? capabilityHighlights(previousPlan.capabilities)
    : []
  const upgradeHighlights =
    previousPlan?.capabilities === undefined ? [] : compareHighlights(baseHighlights, highlights)
  const nextPlanHighlights = nextPlan?.capabilities
    ? capabilityHighlights(nextPlan.capabilities)
    : []
  const nextUpgradeHighlights =
    nextPlan?.capabilities === undefined ? [] : compareHighlights(highlights, nextPlanHighlights)
  const planDescriptionKey = PLAN_DESCRIPTION_KEYS[plan.level as keyof typeof PLAN_DESCRIPTION_KEYS]
  // Drafted tier: priced at nothing and not sellable. Keyed off the *level*, never
  // off an empty `prices[]` — FREE is priceless too and is a real, default plan.
  const isComingSoon = plan.level === PlanLevel.PLAN_LEVEL_ENTERPRISE
  // Never the coming-soon card: "no plan below me" is also what a card looks
  // like when it falls out of `orderedPlans`, so the two must stay disjoint.
  const isFreePlan = !isComingSoon && previousPlan === undefined
  const paidTrustItems = ['cancelAnytime', 'securePayments', 'noCommitment'] as const
  const algoInfoLabel = t('algoInfoLabel')
  const featureInfoLabel = t('featureInfoLabel')
  const algoTitle = t('supportedAlgos')
  const planLimitTitle = t('planLimit')
  const newLabel = t('new')
  const formatAlgoInfoItems = (
    algos: AlgoType[] | undefined,
    baseAlgos: AlgoType[] | undefined = []
  ): FeatureInfoItem[] => {
    const baseSet = new Set(baseAlgos)
    return (algos ?? [])
      .map((algo): FeatureInfoItem | null => {
        const key = ALGO_NAME_KEYS[algo as keyof typeof ALGO_NAME_KEYS]
        if (key === undefined) return null

        const isNew = !baseSet.has(algo)
        return {
          key: String(algo),
          label: t(key),
          isNew,
          newLabel: isNew ? newLabel : undefined,
        }
      })
      .filter((item): item is FeatureInfoItem => item !== null)
  }
  const totalInfoFor = (row: CapabilityHighlight): string | null => {
    if (row.kind !== 'count') return null

    if (row.key === 'tradingAccounts') {
      return t('featureTotals.tradingAccounts', { count: row.count })
    }
    if (row.key === 'accountSharing') {
      return t('featureTotals.accountSharing', { count: row.count })
    }
    if (row.key === 'venues') {
      return t('featureTotals.venues', { count: row.count })
    }
    if (row.key === 'algoTypes') {
      return t('featureTotals.algoTypes', { count: row.count })
    }

    return null
  }

  const withInfo = (
    item: FeatureListItem,
    infoItems: FeatureInfoItem[],
    infoTitle = planLimitTitle,
    infoLabel = featureInfoLabel
  ): FeatureListItem => {
    if (infoItems.length === 0) return item

    return {
      ...item,
      infoItems,
      infoLabel,
      infoTitle,
    }
  }

  const withAlgoInfo = (
    item: FeatureListItem,
    row: CapabilityHighlight,
    algos: AlgoType[] | undefined,
    baseAlgos: AlgoType[] | undefined = []
  ): FeatureListItem => {
    const totalInfo = totalInfoFor(row)
    const infoItems: FeatureInfoItem[] = [
      ...(totalInfo ? [{ key: 'total', label: totalInfo }] : []),
      ...formatAlgoInfoItems(algos, baseAlgos),
    ]

    return withInfo(item, infoItems, algoTitle, algoInfoLabel)
  }

  const withTotalInfo = (item: FeatureListItem, row: CapabilityHighlight): FeatureListItem => {
    const totalInfo = totalInfoFor(row)
    return totalInfo ? withInfo(item, [{ key: 'total', label: totalInfo }]) : item
  }

  const formatIncludedHighlight = (row: CapabilityHighlight): FeatureListItem | null => {
    if (row.kind === 'bool') {
      return row.value
        ? { key: row.key, text: t(row.labelKey as 'features.tradingAccounts') }
        : null
    }
    if (row.count <= 0) return null

    if (row.key === 'tradingAccounts') {
      return { key: row.key, text: t('featureCopy.tradingAccounts', { count: row.count }) }
    }
    if (row.key === 'accountSharing') {
      return { key: row.key, text: t('featureCopy.accountSharing', { count: row.count }) }
    }
    if (row.key === 'venues') {
      return { key: row.key, text: t('featureCopy.venues', { count: row.count }) }
    }
    if (row.key === 'algoTypes') {
      return withAlgoInfo(
        { key: row.key, text: t('featureCopy.algoTypes', { count: row.count }) },
        row,
        plan.capabilities?.allowedAlgos,
        plan.capabilities?.allowedAlgos
      )
    }

    return { key: row.key, text: t(row.labelKey as 'features.tradingAccounts') }
  }

  const formatUpgradeHighlight = (
    row: CapabilityHighlight,
    baseRows: CapabilityHighlight[] = [],
    algos: AlgoType[] | undefined = plan.capabilities?.allowedAlgos,
    baseAlgos: AlgoType[] | undefined = previousPlan?.capabilities?.allowedAlgos
  ): FeatureListItem | null => {
    const base = baseRows.find((item) => item.key === row.key)
    if (row.kind === 'bool') {
      return row.value
        ? { key: row.key, text: t(row.labelKey as 'features.tradingAccounts') }
        : null
    }

    const baseCount = base?.kind === 'count' ? base.count : 0
    const delta = Math.max(0, row.count - baseCount)
    if (delta <= 0) return null

    if (row.key === 'tradingAccounts') {
      return withTotalInfo(
        { key: row.key, text: t('featureCopy.moreTradingAccounts', { count: delta }) },
        row
      )
    }
    if (row.key === 'accountSharing') {
      return withTotalInfo(
        { key: row.key, text: t('featureCopy.moreAccountSharing', { count: delta }) },
        row
      )
    }
    if (row.key === 'venues') {
      return withTotalInfo(
        { key: row.key, text: t('featureCopy.moreVenues', { count: delta }) },
        row
      )
    }
    if (row.key === 'algoTypes') {
      return withAlgoInfo(
        { key: row.key, text: t('featureCopy.moreAlgoTypes', { count: delta }) },
        row,
        algos,
        baseAlgos
      )
    }

    return {
      key: row.key,
      text: t('featureCopy.moreGeneric', {
        count: delta,
        feature: t(row.labelKey as 'features.tradingAccounts'),
      }),
    }
  }
  const includedItems = highlights
    .map(formatIncludedHighlight)
    .filter((item): item is FeatureListItem => item !== null)
  const upgradeItems = upgradeHighlights
    .map((row) => formatUpgradeHighlight(row, baseHighlights))
    .filter((item): item is FeatureListItem => item !== null)
  const nextUpgradeItems = nextUpgradeHighlights
    .map((row) =>
      formatUpgradeHighlight(
        row,
        highlights,
        nextPlan?.capabilities?.allowedAlgos,
        plan.capabilities?.allowedAlgos
      )
    )
    .filter((item): item is FeatureListItem => item !== null)

  const ctaClassName = 'w-full text-base'
  const sectionClassName = 'border-foreground/10 border-t px-6 py-6 md:px-8'

  return (
    <div
      className={cn(
        'relative isolate flex min-w-0 flex-col md:row-span-3 md:grid md:grid-rows-subgrid md:gap-0',
        hiddenOnMobile && 'max-md:hidden'
      )}
    >
      {/* Header cell fades in from the top like orderx.com/pricing; the recommended
          plan's fade is cyan instead of foreground. */}
      <div
        className={cn(
          'flex flex-col gap-1 bg-linear-to-b from-transparent px-6 py-6 md:px-8',
          highlighted ? 'to-ox-cyan/10' : 'to-foreground/10'
        )}
      >
        <h2 className="font-display text-foreground text-[28px] leading-[1.1]">{plan.label}</h2>
        {planDescriptionKey && (
          <p className="text-foreground/70 text-sm">{t(planDescriptionKey)}</p>
        )}
      </div>

      <div className={cn(sectionClassName, 'flex flex-col justify-between gap-4')}>
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          {isComingSoon ? (
            // No price to show, and "$0" would read as free.
            <span className="font-display text-foreground text-4xl leading-none">
              {t('comingSoon')}
            </span>
          ) : (
            <>
              <span className="font-display text-foreground text-4xl leading-none">
                {displayPrice}
              </span>
              <span className="text-foreground/70 text-sm">/{displayInterval}</span>
            </>
          )}
          {price &&
            price.compareAtAmount &&
            Number(price.compareAtAmount) > Number(price.unitAmount) && (
              <span className="text-foreground/40 text-sm line-through">
                {t('was', {
                  price: formatStripeAmount(price.compareAtAmount, price.currency, locale),
                })}
              </span>
            )}
        </div>

        {/* Checked before `cta.kind`: the tier cannot be bought in any context (public
            pricing page, signed-in upgrade or live subscriber), so no CTA renders at all —
            the price slot above already says "coming soon", and a disabled button repeating
            it read as the same line twice. */}
        {isComingSoon ? null : cta.kind === 'current' ? (
          <Button variant="secondary" shape="pill" className={ctaClassName} disabled>
            {t('currentPlanCta')}
          </Button>
        ) : cta.kind === 'managed' ? (
          // Live subscriber: all tier/interval changes happen in the Billing
          // Portal (reached via the "Manage subscription" button), so checkout
          // is disabled here — the backend rejects a second checkout anyway.
          <Button variant="secondary" shape="pill" className={ctaClassName} disabled>
            {t('changeInPortal')}
          </Button>
        ) : cta.kind === 'link' ? (
          <Button
            asChild
            variant={highlighted ? 'basic' : 'default'}
            shape="pill"
            className={ctaClassName}
          >
            <Link href={cta.href}>{cta.label}</Link>
          </Button>
        ) : (
          <Button
            variant={highlighted ? 'basic' : 'default'}
            shape="pill"
            className={ctaClassName}
            onClick={() => price && cta.onChoose(price)}
            disabled={!price || cta.choosingPriceId !== null}
          >
            {!!price && cta.choosingPriceId === price.priceId && (
              <Loader2Icon className="size-4 animate-spin" />
            )}
            {trialOffer
              ? t('trialCta', { days: plan.trialPeriodDays })
              : t('selectPlan', { plan: plan.label })}
          </Button>
        )}
      </div>

      <div className={cn(sectionClassName, 'flex flex-1 flex-col gap-4')}>
        {isComingSoon ? (
          // Static copy, and no payment-benefit list: "Cancel anytime" and
          // "Secure payments" promise a checkout this card does not have.
          <section className="flex flex-col gap-3">
            {previousPlanLabel ? (
              <h3 className="text-foreground text-base font-medium">
                {t.rich('everythingInPlanPlus', {
                  plan: previousPlanLabel,
                  planName: (chunks) => <span>{chunks}</span>,
                })}
              </h3>
            ) : null}
            <ul className="flex flex-col gap-2">
              {ENTERPRISE_HIGHLIGHT_KEYS.map((key) => (
                <FeatureItem key={key} item={{ key, text: t(key) }} />
              ))}
            </ul>
          </section>
        ) : isFreePlan ? (
          <>
            <section className="flex flex-col gap-3">
              <h3 className="text-foreground text-base font-medium">{t('includes')}</h3>
              <ul className="flex flex-col gap-2">
                {includedItems.map((item) => (
                  <FeatureItem key={item.key} item={item} />
                ))}
              </ul>
            </section>

            {nextPlanLabel && nextUpgradeItems.length > 0 ? (
              <section className="border-foreground/10 flex flex-col gap-3 border-t pt-4">
                <h3 className="text-foreground text-base font-medium">
                  {t.rich('availableInPlan', {
                    plan: nextPlanLabel,
                    planName: (chunks) => <span className="text-ox-cyan">{chunks}</span>,
                  })}
                </h3>
                <UpsellTeaser items={nextUpgradeItems} planLabel={nextPlanLabel} />
              </section>
            ) : null}
          </>
        ) : (
          <>
            <section className="flex flex-col gap-3">
              {previousPlanLabel ? (
                <h3 className="text-foreground text-base font-medium">
                  {t.rich('everythingInPlanPlus', {
                    plan: previousPlanLabel,
                    planName: (chunks) => <span>{chunks}</span>,
                  })}
                </h3>
              ) : null}
              <ul className="flex flex-col gap-2">
                {upgradeItems.map((item) => (
                  <FeatureItem key={item.key} item={item} />
                ))}
              </ul>
            </section>

            <ul className="border-foreground/10 flex flex-col gap-2 border-t pt-4">
              {paidTrustItems.map((item) => (
                <li key={item} className="text-foreground/60 flex items-center gap-4 text-sm">
                  <CheckIcon className="size-4 shrink-0" aria-hidden />
                  <span>{t(PAYMENT_BENEFIT_KEYS[item])}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  )
}
